/**
 * Service zum Laden und Verwalten der Anwendungskonfiguration
 *
 * Lädt Konfiguration aus JSON-Datei, validiert sie zur Laufzeit gegen das
 * Zod-Schema (config.schema.ts, fail-fast bei defekter Deployment-Config)
 * und bietet typsicheren Zugriff auf Konfigurationswerte.
 *
 * @class ConfigService
 */
import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DocumentFiltersInput } from '@app/openapi/chatbot-assistant-openapi';
import { forkJoin, lastValueFrom, Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigSchema } from '../schemas/config.schema';

/**
 * Logging-Konfiguration
 */
export interface LoggingConfig {
  level: number;
}

/**
 * Authentifizierungs-Konfiguration
 */
export interface AuthConfig {
  authority: string;
  clientId: string;
  scope: string;
  logLevel: number;
  renewTimeBeforeTokenExpiresInSeconds: number;
}

/**
 * Chatbot API Konfiguration
 */
export interface ChatbotConfig {
  apiBaseUrl: string;
  /**
   * API-Version für SSE-Endpunkte
   * @default 'v1'
   */
  apiVersion?: string;
  /**
   * SSE Stream Timeout in Millisekunden
   * @default 300000 (5 Minuten)
   */
  sseStreamTimeoutMs?: number;
  /**
   * Standard HTTP Request Timeout in Millisekunden
   * @default 30000 (30 Sekunden)
   */
  defaultTimeoutMs?: number;
  /**
   * Toggle fuer den Nachrichten-Sendepfad:
   * - true  → Server-Sent-Events (POST /messages/stream)
   * - false → klassischer REST-Call (POST /messages)
   * @default true
   */
  streamingEnabled?: boolean;
  /**
   * Auth-Modus fuer Chatbot-Requests.
   * - 'bearer': OIDC-Bearer-Token.
   * - 'x-user-id': zusaetzlich X-User-ID aus JWT.sub (altes Dev-Backend).
   * @default 'bearer'
   */
  authMode?: 'bearer' | 'x-user-id';
}

/**
 * Portal-Integrations-Konfiguration
 */
export interface PortalConfig {
  targetOrigin: string;
}

/**
 * Sentry Error Tracking Konfiguration
 */
export interface SentryConfig {
  enabled: boolean;
  debug: boolean;
  dsn: string;
  sampleRate: number;
}

/**
 * Matomo Analytics Konfiguration
 */
export interface MatomoConfig {
  enabled: boolean;
  trackerUrl: string;
  siteId: string;
}

/**
 * Chat Feature Konfiguration
 */
export interface ChatConfig {
  /**
   * Standard-Tags für Query Filters
   *
   * Flexible Tag-Konfiguration für RAG-Suche und Antwort-Filterung.
   * Wird automatisch bei jeder Chat-Nachricht mitgesendet.
   *
   * Werte können ein einzelnes Tag (string) oder mehrere (string[]) sein,
   * z. B. mehrere Produkte. Entspricht `DocumentFiltersInput.tags`.
   *
   * @example { "category": "Just Farming" }
   * @example { "product": ["ADNOVA+", "ADNOVA Finance"] }
   */
  defaultTags?: Record<string, string | string[]>;
  /**
   * Vorschlagsfragen auf dem Willkommensbildschirm (pro Deployment).
   * Leeres Array → keine Vorschläge.
   */
  suggestions?: string[];
  retryDelayMs?: number;
  message?: {
    maxLength?: number;
  };
  conversation?: {
    titleMaxLength?: number;
  };
  /**
   * System Prompt Override Konfiguration (Prototyp-Feature)
   *
   * Ermoeglicht das Ueberschreiben des System-Prompts pro Request.
   * Default: deaktiviert (sicherheitskritisch).
   */
  systemPromptOverride?: {
    enabled: boolean;
    defaultPromptVersion?: 'assistant_stateful' | 'assistant_stateless';
  };
  /**
   * Feedback-Feature-Gate. Wenn true, rendern Assistenten-Antworten die
   * Feedback-Buttons (Gut/Teilweise/Schlecht).
   *
   * @default true
   */
  feedback?: {
    enabled: boolean;
  };
  /**
   * Debug-Panel-Feature-Gate. Wenn true, zeigt der 3-Punkte-Button an
   * Assistenten-Nachrichten Prompt-Version, Antwortzeit und Message-ID.
   *
   * @default false
   */
  debugPanel?: {
    enabled: boolean;
  };
  /**
   * Feature-Gate fuer die Quellenangaben (Citations) unter Assistenten-Antworten.
   *
   * @default true
   */
  citations?: {
    enabled: boolean;
  };
  /**
   * Feature-Gate fuer Dokument-Upload. UI noch nicht vorhanden - Flag
   * dient als Vorbereitung.
   *
   * @default false
   */
  documentsUpload?: {
    enabled: boolean;
  };
  /**
   * Feature-Gate fuer die Tag-Vergabe beim Dokument-Upload.
   * Greift nur bei aktivem Dokument-Upload.
   *
   * @default false
   */
  tags?: {
    enabled: boolean;
  };

  /**
   * Retriever-Override je Chat-Request (RAG-Gründlichkeit).
   * 0 = kein Override (Backend-Default gilt).
   */
  retriever?: {
    /** Anzahl der Chunks, die in den Prompt weitergegeben werden (1-100). */
    topK: number;
    /** Minimaler Score (0-1). */
    scoreThreshold: number;
    /** Anzahl Kandidaten fürs Reranking aus dem Vector Store (1-200, API 0.23.0+). */
    candidateCount?: number;
  };
}

/**
 * Resilience & Fehlertoleranz Konfiguration
 */
export interface ResilienceConfig {
  circuitBreaker?: {
    failureThreshold?: number;
    successThreshold?: number;
    timeoutMs?: number;
  };
  retry?: {
    maxRetries?: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffMultiplier?: number;
  };
}

/**
 * Vollständiges Anwendungskonfigurations-Interface
 */
export interface IConfig {
  environment: string;
  logging: LoggingConfig;
  auth: AuthConfig;
  chatbot: ChatbotConfig;
  chat?: ChatConfig;
  resilience?: ResilienceConfig;
  portal: PortalConfig;
  sentry: SentryConfig;
  matomo: MatomoConfig;
}

/**
 * Unveränderbare Konfigurationsklasse
 */
export class Config implements IConfig {
  public readonly environment: string;
  public readonly logging: LoggingConfig;
  public readonly auth: AuthConfig;
  public readonly chatbot: ChatbotConfig;
  public readonly chat?: ChatConfig;
  public readonly resilience?: ResilienceConfig;
  public readonly portal: PortalConfig;
  public readonly sentry: SentryConfig;
  public readonly matomo: MatomoConfig;

  /**
   * Erstellt eine unveränderbare Config Instanz
   *
   * @param {IConfig} config Konfigurationsobjekt
   */
  constructor(config: IConfig) {
    this.environment = config.environment;
    this.logging = config.logging;
    this.auth = config.auth;
    this.chatbot = config.chatbot;
    this.chat = config.chat;
    this.resilience = config.resilience;
    this.portal = config.portal;
    this.sentry = config.sentry;
    this.matomo = config.matomo;
  }
}

@Injectable()
export class ConfigService implements OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private _httpClient = inject(HttpClient);

  /**
   * ReplaySubject zum Broadcasen der geladenen Konfiguration
   */
  private _loaded = new ReplaySubject<Config>();

  /**
   * Gecachte Konfigurationsinstanz
   */
  private _config_?: Config;

  /**
   * Angular Lifecycle Hook für Cleanup
   *
   * Schließt das Konfigurations-Observable bei Service-Zerstörung ab.
   */
  ngOnDestroy() {
    this._loaded.complete();
  }

  /**
   * Observable der geladenen Konfiguration
   *
   * Meldet automatisch ab wenn Component zerstört wird.
   *
   * @returns {Observable<Config>} Konfigurations-Observable
   */
  get loaded$() {
    return this._loaded.pipe(takeUntilDestroyed(this._destroyRef));
  }

  /**
   * Gibt die geladene Konfiguration zurück
   *
   * @returns {Config} Die geladene Konfiguration
   * @throws {Error} Falls Konfiguration noch nicht geladen
   */
  get config(): Config {
    if (!this._config_) {
      throw new Error(
        'Configuration not loaded. Please ensure loadConfig() is called during application initialization.',
      );
    }
    return this._config_;
  }

  /**
   * Non-throwing Variante fuer Code-Pfade, die auch vor APP_INITIALIZER
   * laufen koennen (z.B. HTTP-Interceptor fuer den Config-Fetch selbst).
   *
   * @returns Config oder undefined solange noch nicht geladen
   */
  get configOrUndefined(): Config | undefined {
    return this._config_;
  }

  /**
   * Lädt Konfiguration aus JSON-Datei
   *
   * Sollte während Anwendungsinitialisierung aufgerufen werden (APP_INITIALIZER).
   *
   * @returns {Promise<[Config]>} Promise das auflöst wenn Konfiguration geladen ist
   */
  loadConfig(): Promise<[Config]> {
    return lastValueFrom(forkJoin([this.getConfig()]));
  }

  /**
   * Holt Konfiguration aus JSON-Datei
   *
   * Lädt Konfiguration aus /assets/config/config.json, validiert sie gegen das
   * Zod-Schema (Regel 3: fail-fast bei defekter Deployment-Config, bevor die
   * App mit kaputten Werten startet) und cached sie.
   *
   * @returns {Observable<Config>} Observable der geladenen Konfiguration
   */
  getConfig(): Observable<Config> {
    const url = '/assets/config/config.json';
    return this._httpClient.get<IConfig>(url).pipe(
      tap((config) => {
        // Validierungs-Gate: das rohe JSON bleibt die Quelle (keine Zod-Defaults
        // materialisieren), safeParse dient nur der Struktur-/Typ-Pruefung.
        const validation = ConfigSchema.safeParse(config);
        if (!validation.success) {
          const issues = validation.error.issues
            .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
            .join('; ');
          throw new Error(`Ungültige Konfiguration (config.json): ${issues}`);
        }
        this._config_ = new Config(config);
        this._loaded.next(this._config_);
      }),
    );
  }

  /**
   * Liefert Default Query Filters basierend auf Config
   *
   * Extrahiert die konfigurierten `defaultTags` aus der Chat-Konfiguration
   * und wandelt sie in das DocumentFilters-Format um.
   *
   * @returns DocumentFilters mit konfigurierten Tags, oder leeres Objekt
   * @example
   * // Config: { chat: { defaultTags: { category: "Just Farming" } } }
   * getDefaultDocumentFilters()
   * // → { tags: { category: "Just Farming" } }
   */
  public getDefaultDocumentFilters(): DocumentFiltersInput {
    const tags = this.config.chat?.defaultTags;

    // Nur Tags zurückgeben wenn konfiguriert und nicht leer
    if (tags && Object.keys(tags).length > 0) {
      return { tags };
    }

    return {};
  }

  /**
   * Retriever-Override für Chat-Requests (chat.retriever, Deployment-Config).
   *
   * Steuert die RAG-Gründlichkeit: topK = Anzahl der Chunks, die der Retriever
   * in den Prompt gibt (Backend-Default ist knapp – bei Aggregationsfragen über
   * viele Dokumente gehen sonst Fundstellen verloren). 0 bedeutet „kein
   * Override senden" (Backend-Default gilt); dann undefined.
   *
   * @returns RetrieverOverride-kompatibles Objekt oder undefined
   */
  public getRetrieverOverride(): { topK?: number; scoreThreshold?: number; candidateCount?: number } | undefined {
    const retriever = this.config.chat?.retriever;
    const override: { topK?: number; scoreThreshold?: number; candidateCount?: number } = {};

    if (retriever?.topK && retriever.topK > 0) {
      override.topK = retriever.topK;
    }
    if (retriever?.scoreThreshold && retriever.scoreThreshold > 0) {
      override.scoreThreshold = retriever.scoreThreshold;
    }
    if (retriever?.candidateCount && retriever.candidateCount > 0) {
      override.candidateCount = retriever.candidateCount;
    }

    return Object.keys(override).length > 0 ? override : undefined;
  }
}
